import React, { useRef, useMemo } from "react";
import { connect } from "react-redux";
import { setState, StoreState } from "Store";
import { ColumnConfig } from "Table/interfaces";
import {
    ColumnContainer,
    FixedColumnContainer,
    FixedHeaderContainer,
    HeadContainer,
    StyledHeader,
    StyledTable,
    TableBodyContainer,
    TableContainer,
    TableHeaderContainer,
} from "./style";
import TableSection from "./TableSection";
import RowElement from "./TableSection/RowElement";
import { SetDimsAction, SetHoverAction, SET_HOVER } from "./TableSection/RowElement/actionTypes";
import { TableRow } from "./interfaces";

interface Dimension {
    width: number[];
    height: number[];
}

export interface TableProps {
    columnConfig: ColumnConfig;
    height: string;
    header?: TableRow;
    rows: TableRow[];
}

const mapStateToProps = (state: StoreState) => ({
    hoverIndexes: state.hoverIndexes,
    dims: state.tableDims,
    fixedDims: state.fixedTableDims,
});

const mapDispatchToProps = (dispatch: (dispatch: SetHoverAction | SetDimsAction) => void) => ({
    onHover: (row: number, col: number) => {
        dispatch(setState(SET_HOVER, { data: { row, col } }));
    },
});

type Props = TableProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const TableComponent: React.FC<Props> = ({
    header,
    rows = [],
    columnConfig = { numFixed: 0, size: 200 },
    fixedDims = { height: [], width: [] },
    dims = { height: [], width: [] },
    height = "500px",
    onHover,
}) => {
    let scrolling = false;
    const headerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const contentFixedRef = useRef<HTMLDivElement>(null);

    const handleScroll = (target: HTMLDivElement, type: string) => {
        if (!scrolling) {
            window.requestAnimationFrame(() => {
                doScroll(target, type);
                scrolling = false;
            });
            scrolling = true;
        }
    };

    const doScroll = (target: HTMLDivElement, type: string) => {
        switch (type) {
            case "content":
                if (headerRef.current && contentFixedRef.current) {
                    headerRef.current.scrollLeft = target.scrollLeft;
                    contentFixedRef.current.scrollTop = target.scrollTop;
                }
                break;
            case "fixedContent":
                if (contentRef.current) {
                    contentRef.current.scrollTop = target.scrollTop;
                }
                break;
            case "header":
                if (contentRef.current) {
                    contentRef.current.scrollLeft = target.scrollLeft;
                }
                break;
        }
    };

    const splitRows = (header: TableRow | undefined, rows: TableRow[]) => {
        let headerCopy: TableRow = { cells: Array().concat(header?.cells || []) };
        let rowsCopy: TableRow[] = Array().concat(
            rows.map(row => {
                return { height: row.height, cells: Array().concat(row.cells) };
            })
        );

        let fixedHeader: TableRow = {
            cells: headerCopy.cells.splice(0, columnConfig.numFixed),
        };

        let fixedRows: TableRow[] = rowsCopy.map((row: TableRow) => {
            return {
                height: row.height,
                cells: row.cells.splice(0, columnConfig.numFixed),
            };
        });

        return {
            header: headerCopy,
            fixedHeader: fixedHeader,
            rows: rowsCopy,
            fixedRows: fixedRows,
        };
    };

    const getDimensions = (): Dimension | undefined => {
        if ((fixedDims.width.length && fixedDims.height.length) || columnConfig.numFixed === 0) {
            return {
                width: fixedDims.width.concat(dims.width),
                height: fixedDims.height.map((height: number, i: number) => Math.max(height, dims.height[i])),
            };
        }
        return undefined;
    };

    let data = useMemo(() => splitRows(header, rows), [header, rows]);
    let dimensions = useMemo(getDimensions, [fixedDims, dims]);
    let leftPadding: number = new Array(columnConfig.numFixed).fill(1).reduce((sum: number, _: number, i: number) => {
        return sum + (dimensions?.width[i] ?? 0);
    }, 0);

    return (
        <TableContainer height={height}>
            <TableHeaderContainer leftPadding={leftPadding} onMouseOver={() => onHover(-1, -1)}>
                <FixedHeaderContainer>
                    {/* Table Header */}
                    <StyledTable>
                        <StyledHeader>
                            <RowElement
                                header
                                fixed
                                row={data.fixedHeader as TableRow}
                                height={dimensions?.height[0]}
                                columnWidths={dimensions?.width}
                                rowIndex={0}
                            />
                        </StyledHeader>
                    </StyledTable>
                </FixedHeaderContainer>
                <HeadContainer onScroll={e => handleScroll(e.target as HTMLDivElement, "header")} ref={headerRef}>
                    <StyledTable>
                        {/* Table Header */}
                        <StyledHeader>
                            <RowElement
                                header
                                row={data.header as TableRow}
                                height={dimensions?.height[0]}
                                columnWidths={dimensions?.width}
                                rowIndex={0}
                            />
                        </StyledHeader>
                    </StyledTable>
                </HeadContainer>
            </TableHeaderContainer>

            <TableBodyContainer leftPadding={leftPadding}>
                <FixedColumnContainer
                    onMouseOver={() => onHover(-1, -1)}
                    onScroll={e => handleScroll(e.target as HTMLDivElement, "fixedContent")}
                    ref={contentFixedRef}
                >
                    <TableSection
                        data={data.fixedRows}
                        fixed={true}
                        rowHeights={dimensions?.height}
                        columnWidths={dimensions?.width}
                    />
                </FixedColumnContainer>
                <ColumnContainer ref={contentRef} onScroll={e => handleScroll(e.target as HTMLDivElement, "content")}>
                    <TableSection data={data.rows} rowHeights={dimensions?.height} columnWidths={dimensions?.width} />
                </ColumnContainer>
            </TableBodyContainer>
        </TableContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(TableComponent);
